import React, { useState, useCallback, useEffect } from 'react';
import css from './CropperModal.module.css';
import Cropper from 'react-easy-crop';
import ReactSlider from 'react-slider';
import CustomModal from '../CustomModal/CustomModal';
import Button from '../Button/Button';
import Icons from '../Icons/Icons';
import { useIntl } from 'react-intl';
import { CropperShape } from '../../util/enums';

const CropperModal = ({
  isOpen,
  onClose,
  onSave,
  acceptedTypes,
  imageFile,
  aspectRatio = 16 / 9, // default to cover image aspect ratio
  isProfileImage = false, // flag to determine if it's profile or cover image
}) => {
  const { formatMessage } = useIntl();
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [uploadInProgress, setUploadInProgress] = useState(false);

  // Determine aspect ratio based on image type
  const getAspectRatio = () => {
    if (isProfileImage) {
      return 1; // Square for profile images
    }
    return aspectRatio; // Wide for cover images (16:9 or custom)
  };

  // Determine crop shape
  const getCropShape = () => {
    if (isProfileImage) {
      return CropperShape.ROUND; // Circular for profile images
    }
    return CropperShape.RECTANGLE; // Rectangle for cover images
  };

  useEffect(() => {
    if (!imageFile) {
      setImageSrc(null);
      setFileName(null);
      return;
    }

    const url = URL.createObjectURL(imageFile);
    setImageSrc(url);
    setFileName(imageFile.name);

    return () => {
      URL.revokeObjectURL(url);
      setImageSrc(null);
      setFileName(null);
    };
  }, [imageFile]);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileChange = event => {
    const file = event.target.files[0];
    if (file) {
      setFileName(file.name);
      const reader = new FileReader();
      reader.onload = () => setImageSrc(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const getCroppedImg = async (imageSrc, pixelCrop, fileName) => {
    const image = new Image();
    image.src = imageSrc;
    await new Promise(resolve => {
      image.onload = resolve;
    });

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise(resolve => {
      canvas.toBlob(blob => {
        const croppedFile = new File([blob], fileName, { type: 'image/jpeg' });
        resolve(croppedFile);
      }, 'image/jpeg');
    });
  };

  const handleSave = async () => {
    try {
      setUploadInProgress(true);
      if (!imageSrc || !croppedAreaPixels || !fileName) return;
      const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels, fileName);
      onSave(croppedImage);
    } catch (error) {
      // Optionally show error toast
    } finally {
      setUploadInProgress(false);
      setImageSrc(null);
      setZoom(1);
      setCrop({ x: 0, y: 0 });
      setCroppedAreaPixels(null);
      setFileName(null);
      onClose();
    }
  };

  const handleCancel = () => {
    setImageSrc(null);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    setCroppedAreaPixels(null);
    setFileName(null);
    onClose();
  };

  return (
    <CustomModal
      id="CropperModal"
      isOpen={isOpen}
      onClose={handleCancel}
      modalTitle={formatMessage({ id: `CropperModal.title` })}
    >
      <div className={css.cropperModalContent}>
        {!imageSrc ? (
          <div className={css.uploadSection}>
            <Icons name="uploadImgIcon" />
            <p className={css.uploadBtn}>{formatMessage({ id: 'CropperModal.addPhoto' })}</p>
            <label htmlFor="image-upload" className={css.uploadLabel}>
              {formatMessage({ id: 'CropperModal.uploadImage' })}
            </label>
            <input
              id="image-upload"
              type="file"
              accept={Object.keys(acceptedTypes).join(',')}
              onChange={handleFileChange}
              className={css.fileInput}
            />
          </div>
        ) : (
          <>
            <div className={css.cropperContainer}>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={getAspectRatio()}
                cropShape={getCropShape()}
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
                objectFit="contain" // This ensures the full image is visible
                cropSize={{ width: 400, height: isProfileImage ? 400 : 225 }} // Set crop area size
              />
            </div>
            <div className={css.zoomSlider}>
              <label>{formatMessage({ id: 'CropperModal.zoom' })}</label>
              <ReactSlider
                className={css.slider}
                thumbClassName={css.thumb}
                trackClassName={css.track}
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                onChange={value => setZoom(value)}
              />
            </div>
            <div className={css.buttonContainer}>
              <Button
                type="button"
                inProgress={uploadInProgress}
                className={css.saveButton}
                onClick={handleSave}
              >
                {formatMessage({ id: 'CropperModal.save' })}
              </Button>
            </div>
          </>
        )}
      </div>
    </CustomModal>
  );
};

export default CropperModal;
